import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  AppSvgIconComponent,
  OverlayService,
  SignalButton,
  SignalCheckbox,
  State,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import { PageHeaderComponent } from '@core/components/page-header/page-header.component';
import { WorkspaceAPIService } from '@core/api/workspace/services/workspace-api/workspaceAPI.service';
import type { DataLibraryCollectionDataResponse } from '@core/api/workspace/models/dataLibraryCollectionDataResponse';
import type { DataLibraryCollectionFieldResponse } from '@core/api/workspace/models/dataLibraryCollectionFieldResponse';
import type { GetDataLibraryCollectionApiResponse } from '@core/api/workspace/models/getDataLibraryCollectionApiResponse';
import type { ListDataLibraryCollectionItemsApiResponse } from '@core/api/workspace/models/listDataLibraryCollectionItemsApiResponse';
import {
  EditCollectionColumnsOverlayComponent,
  type EditCollectionColumnsOverlayData,
  type EditCollectionColumnsOverlayResult,
} from '../edit-collection-columns-overlay/edit-collection-columns-overlay';

type CellValue = string | number | boolean | null;

interface RowDraft {
  /** Server id when persisted; null for new rows. */
  id: string | null;
  localId: number;
  values: Record<string, CellValue>;
  dirty: boolean;
}

let nextLocalRowId = 1;

function emptyValues(
  fields: DataLibraryCollectionFieldResponse[],
): Record<string, CellValue> {
  const values: Record<string, CellValue> = {};
  for (const field of fields) {
    values[field.key] = field.valueType === 'boolean' ? false : null;
  }
  return values;
}

function cellKey(localId: number, fieldKey: string): string {
  return `${localId}:${fieldKey}`;
}

function isEmptyCellValue(value: CellValue): boolean {
  return value === null || value === undefined || value === '';
}

function normalizeUniqueCompareKey(value: CellValue): string {
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  return String(value);
}

@Component({
  selector: 'app-collection-detail-page',
  standalone: true,
  imports: [AppSvgIconComponent, PageHeaderComponent, SignalButton, SignalCheckbox],
  templateUrl: './collection-detail-page.html',
  styleUrl: './collection-detail-page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CollectionDetailPage extends BaseComponent implements OnInit {
  private readonly userAPIService = inject(WorkspaceAPIService);
  private readonly overlayService = inject(OverlayService);

  readonly collectionState = new State<GetDataLibraryCollectionApiResponse>();
  readonly itemsState = new State<ListDataLibraryCollectionItemsApiResponse>();

  readonly collection = signal<DataLibraryCollectionDataResponse | null>(null);
  readonly rows = signal<RowDraft[]>([]);
  readonly deletedIds = signal<string[]>([]);
  readonly saving = signal(false);
  readonly invalidCells = signal(new Set<string>());

  readonly headerDescription = computed(() => {
    const key = this.collection()?.key;
    return key ? `Key ${key}` : undefined;
  });

  readonly hasUnsavedChanges = computed(
    () =>
      this.deletedIds().length > 0 ||
      this.rows().some((row) => row.dirty || row.id == null),
  );

  private collectionId = '';

  ngOnInit(): void {
    this.collectionId = this.route.snapshot.paramMap.get('id') ?? '';
    if (!this.collectionId) {
      void this.router.navigate(['/data-library']);
      return;
    }
    this.loadCollection();
  }

  openEditColumns(): void {
    const current = this.collection();
    if (!current) {
      return;
    }

    const dialogRef = this.overlayService.openBottomSheet(
      EditCollectionColumnsOverlayComponent,
      {
        data: {
          collectionId: current.id,
          name: current.name,
          fields: current.fields,
          uniqueFieldKey: current.uniqueFieldKey,
        } satisfies EditCollectionColumnsOverlayData,
        panelClass: [
          'bg-white',
          'shadow-2',
          'w-full',
          'h-[90%]',
          'max-h-[90dvh]',
          'rounded-t-3xl',
          'overflow-hidden',
        ],
      },
    );

    dialogRef.closed.subscribe(
      (result: EditCollectionColumnsOverlayResult | null | undefined) => {
        if (result) {
          this.collection.set(result);
          this.syncRowsToFields(result.fields);
          this.invalidCells.set(new Set());
        }
      },
    );
  }

  addRow(): void {
    const fields = this.collection()?.fields ?? [];
    const localId = nextLocalRowId++;
    this.rows.update((rows) => [
      ...rows,
      {
        id: null,
        localId,
        values: emptyValues(fields),
        dirty: true,
      },
    ]);
    setTimeout(() => {
      const cell = document.querySelector(
        `[data-cell^="${localId}:"]`,
      ) as HTMLElement | null;
      cell?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      cell?.focus({ preventScroll: true });
    });
  }

  removeRow(localId: number): void {
    const row = this.rows().find((item) => item.localId === localId);
    if (!row) {
      return;
    }
    if (row.id) {
      this.deletedIds.update((ids) => [...ids, row.id!]);
    }
    this.rows.update((rows) => rows.filter((item) => item.localId !== localId));
    this.invalidCells.update((cells) => {
      const next = new Set<string>();
      for (const key of cells) {
        if (!key.startsWith(`${localId}:`)) {
          next.add(key);
        }
      }
      return next;
    });
  }

  setText(localId: number, key: string, value: string): void {
    this.patchCell(localId, key, value);
  }

  setNumber(localId: number, key: string, raw: string): void {
    if (raw.trim() === '') {
      this.patchCell(localId, key, null);
      return;
    }
    const number = Number(raw);
    this.patchCell(localId, key, Number.isFinite(number) ? number : null);
  }

  setBoolean(localId: number, key: string, checked: boolean): void {
    this.patchCell(localId, key, checked);
  }

  setDate(localId: number, key: string, value: string): void {
    this.patchCell(localId, key, value || null);
  }

  cellAsString(value: CellValue): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  cellAsBoolean(value: CellValue): boolean {
    return value === true;
  }

  isInvalid(localId: number, key: string): boolean {
    return this.invalidCells().has(cellKey(localId, key));
  }

  cellInputClass(localId: number, key: string, minWidthClass: string): string {
    const base = `w-full ${minWidthClass} rounded-lg border bg-white px-2.5 py-1.5 text-sm text-neutral-900 outline-none transition`;
    if (this.isInvalid(localId, key)) {
      return `${base} border-red-500 focus:border-red-500 ring-1 ring-red-200`;
    }
    return `${base} border-neutral-200 focus:border-neutral-400`;
  }

  isFieldRequired(field: DataLibraryCollectionFieldResponse): boolean {
    const uniqueKey = this.collection()?.uniqueFieldKey;
    if (uniqueKey && field.key === uniqueKey) {
      return true;
    }
    return field.required !== false;
  }

  discard(): void {
    if (this.saving()) {
      return;
    }
    this.deletedIds.set([]);
    this.invalidCells.set(new Set());
    this.loadItems();
  }

  async save(): Promise<void> {
    const fields = this.collection()?.fields ?? [];
    const uniqueKey = this.collection()?.uniqueFieldKey;
    if (!uniqueKey) {
      return;
    }

    const invalid = new Set<string>();
    let firstInvalidKey: string | null = null;

    for (const row of this.rows()) {
      for (const field of fields) {
        if (field.valueType === 'boolean') {
          continue;
        }
        const mustFill =
          field.key === uniqueKey || field.required !== false;
        if (!mustFill) {
          continue;
        }
        if (isEmptyCellValue(row.values[field.key])) {
          const key = cellKey(row.localId, field.key);
          invalid.add(key);
          if (!firstInvalidKey) {
            firstInvalidKey = key;
          }
        }
      }
    }

    if (invalid.size > 0) {
      this.invalidCells.set(invalid);
      this.toasterService.error('Please fill in all required fields.');
      this.scrollToCell(firstInvalidKey!);
      return;
    }

    const valueToLocalIds = new Map<string, number[]>();
    for (const row of this.rows()) {
      const uniqueValue = row.values[uniqueKey];
      if (isEmptyCellValue(uniqueValue)) {
        continue;
      }
      const normalized = normalizeUniqueCompareKey(uniqueValue);
      const ids = valueToLocalIds.get(normalized) ?? [];
      ids.push(row.localId);
      valueToLocalIds.set(normalized, ids);
    }

    let firstDuplicateKey: string | null = null;
    for (const ids of valueToLocalIds.values()) {
      if (ids.length < 2) {
        continue;
      }
      for (const localId of ids) {
        const key = cellKey(localId, uniqueKey);
        invalid.add(key);
        if (!firstDuplicateKey) {
          firstDuplicateKey = key;
        }
      }
    }

    if (firstDuplicateKey) {
      this.invalidCells.set(invalid);
      this.toasterService.error(
        'Duplicate unique values found. Each row must have a unique value.',
      );
      this.scrollToCell(firstDuplicateKey);
      return;
    }

    this.invalidCells.set(new Set());
    this.saving.set(true);
    this.loaderService.show();

    try {
      for (const id of this.deletedIds()) {
        await firstValueFrom(
          this.userAPIService.deleteDataLibraryCollectionItem(
            this.collectionId,
            id,
          ),
        );
      }

      for (const row of this.rows()) {
        const values = this.serializeValues(fields, row.values);
        if (!row.id) {
          await firstValueFrom(
            this.userAPIService.createDataLibraryCollectionItem(
              this.collectionId,
              { values },
            ),
          );
        } else if (row.dirty) {
          await firstValueFrom(
            this.userAPIService.patchDataLibraryCollectionItem(
              this.collectionId,
              row.id,
              { values },
            ),
          );
        }
      }

      this.toasterService.success('Rows saved.');
      this.deletedIds.set([]);
      this.loadItems();
    } catch (error: unknown) {
      const message =
        (error as { error?: { message?: string }; message?: string })?.error
          ?.message ||
        (error as { message?: string })?.message ||
        'Failed to save rows.';
      this.toasterService.error(message);
    } finally {
      this.saving.set(false);
      this.loaderService.hide();
    }
  }

  private scrollToCell(key: string): void {
    // Defer so red borders paint before scrolling.
    requestAnimationFrame(() => {
      const el = document.querySelector(
        `[data-cell="${CSS.escape(key)}"]`,
      ) as HTMLElement | null;
      if (!el) {
        return;
      }
      el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      el.focus({ preventScroll: true });
    });
  }

  private loadCollection(): void {
    this.executeRequest({
      state: this.collectionState,
      request: this.userAPIService.getDataLibraryCollection(this.collectionId),
      showLoader: true,
      handleError: true,
      onSuccess: (response) => {
        this.collection.set(response.data);
        this.loadItems();
      },
      onFailed: () => {
        void this.router.navigate(['/data-library']);
      },
    });
  }

  private loadItems(): void {
    this.executeRequest({
      state: this.itemsState,
      request: this.userAPIService.listDataLibraryCollectionItems(
        this.collectionId,
        { pageNumber: 1, pageSize: 100 },
      ),
      showLoader: true,
      handleError: true,
      onSuccess: (response) => {
        const fields = this.collection()?.fields ?? [];
        this.invalidCells.set(new Set());
        this.rows.set(
          response.data.items.map((item) => ({
            id: item.id,
            localId: nextLocalRowId++,
            values: this.mergeValues(
              fields,
              item.values as Record<string, CellValue>,
            ),
            dirty: false,
          })),
        );
      },
    });
  }

  private mergeValues(
    fields: DataLibraryCollectionFieldResponse[],
    values: Record<string, CellValue>,
  ): Record<string, CellValue> {
    const merged = emptyValues(fields);
    for (const field of fields) {
      if (Object.prototype.hasOwnProperty.call(values, field.key)) {
        merged[field.key] = values[field.key] ?? merged[field.key];
      }
    }
    return merged;
  }

  private syncRowsToFields(fields: DataLibraryCollectionFieldResponse[]): void {
    this.rows.update((rows) =>
      rows.map((row) => ({
        ...row,
        values: this.mergeValues(fields, row.values),
        dirty: true,
      })),
    );
  }

  private patchCell(localId: number, key: string, value: CellValue): void {
    this.rows.update((rows) =>
      rows.map((row) => {
        if (row.localId !== localId) {
          return row;
        }
        return {
          ...row,
          dirty: true,
          values: { ...row.values, [key]: value },
        };
      }),
    );

    const invalidKey = cellKey(localId, key);
    if (this.invalidCells().has(invalidKey)) {
      this.invalidCells.update((cells) => {
        const next = new Set(cells);
        next.delete(invalidKey);
        return next;
      });
    }
  }

  private serializeValues(
    fields: DataLibraryCollectionFieldResponse[],
    values: Record<string, CellValue>,
  ): Record<string, string | number | boolean | null> {
    const result: Record<string, string | number | boolean | null> = {};
    for (const field of fields) {
      result[field.key] = values[field.key] ?? null;
    }
    return result;
  }
}
